import React, { useState, Children } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Calendar, MapPin, Users, Check, Star, ChevronDown, ChevronUp, ChevronRight, Share2, Heart, Phone, Mail, ArrowLeft, Plane, Coffee, Utensils, Bus, Hotel, Map } from 'lucide-react';
const TourDetail = () => {
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState('itinerary');
  const [openFaqs, setOpenFaqs] = useState<number[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  // This would come from an API in a real app
  const tourData = {
    id: id,
    name: 'Classic Umrah Journey',
    tagline: 'A spiritual journey to the holy cities of Makkah and Madinah',
    duration: '11 Days',
    cities: '5N MAKKAH | 4N MADINAH',
    groupSize: '15-20 People',
    price: 5057,
    rating: 4.8,
    reviewCount: 124,
    description: 'Experience a deeply spiritual journey with our most popular Umrah package. This carefully designed itinerary allows you to perform your religious obligations while enjoying comfortable accommodations and guided tours of historical Islamic sites.',
    highlights: ['Guided Umrah rituals by experienced religious scholars', '5-star accommodations within walking distance to Haram', 'Visit to historical sites in Makkah and Madinah', 'All-inclusive package with meals and transportation', 'Experienced multilingual guides throughout your journey', 'Pre-trip preparation sessions and educational materials'],
    inclusions: ['Return flights from Singapore', 'Airport transfers', '5-star hotel accommodations', 'Daily breakfast and dinner', 'Guided Umrah and Ziyarah tours', 'Visa processing fees', 'Local transportation', 'Multilingual guide services', 'Pre-departure briefing'],
    exclusions: ['Travel insurance', 'Personal expenses', 'Optional tours', 'Lunch meals', 'PCR tests if required', 'Laundry services'],
    itinerary: [{
      day: 1,
      title: 'Departure from Singapore',
      description: 'Gather at Changi Airport for check-in. Depart for Jeddah on our scheduled flight. Meals and entertainment provided on board.',
      meals: ['dinner'],
      accommodation: 'In-flight'
    }, {
      day: 2,
      title: 'Arrival in Jeddah - Transfer to Makkah',
      description: 'Arrive at King Abdulaziz International Airport. Complete immigration procedures and transfer to your hotel in Makkah. Rest and prepare for Umrah.',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Makkah'
    }, {
      day: 3,
      title: 'Umrah Rituals',
      description: "After Fajr prayer, our guide will lead you through the Umrah rituals including Tawaf, Sa'i, and Tahallul. Evening at leisure for voluntary prayers.",
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Makkah'
    }, {
      day: 4,
      title: 'Makkah - Historical Sites Tour',
      description: 'Visit historical sites including Jabal al-Noor (Cave of Hira), Jabal Thawr, and Hudaibiyah. Return to hotel for rest and prayers.',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Makkah'
    }, {
      day: 5,
      title: 'Makkah - Day at Leisure',
      description: 'Free day for voluntary prayers and personal reflection. Optional visit to Makkah Mall for shopping (additional cost).',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Makkah'
    }, {
      day: 6,
      title: 'Makkah - Day at Leisure',
      description: 'Another day for voluntary worship and personal time. Our guide is available for questions and guidance.',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Makkah'
    }, {
      day: 7,
      title: 'Transfer to Madinah',
      description: 'After Fajr prayer, check out and transfer to Madinah by air-conditioned bus (approximately 5 hours). Check-in to your hotel and rest.',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Madinah'
    }, {
      day: 8,
      title: 'Madinah - Ziyarah Tour',
      description: 'Visit Masjid Nabawi and pay respects at the Raudhah. Afternoon tour of historical sites including Uhud Mountain, Quba Mosque, and Qiblatain Mosque.',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Madinah'
    }, {
      day: 9,
      title: 'Madinah - Day at Leisure',
      description: 'Free day for worship at Masjid Nabawi and personal reflection. Optional shopping trip to souvenir shops nearby (additional cost).',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Madinah'
    }, {
      day: 10,
      title: 'Madinah - Day at Leisure',
      description: 'Final full day in Madinah for worship and personal time. Farewell dinner in the evening.',
      meals: ['breakfast', 'dinner'],
      accommodation: '5-star hotel in Madinah'
    }, {
      day: 11,
      title: 'Return to Singapore',
      description: 'After Fajr prayer, check out and transfer to Madinah Airport for your return flight to Singapore. Arrive in Singapore and end of services.',
      meals: ['breakfast'],
      accommodation: 'N/A'
    }],
    faqs: [{
      question: 'What is the visa process for Umrah?',
      answer: "We handle the entire visa process for you. You'll need to provide a valid passport with at least 6 months validity, passport-sized photos, and completed application forms. Our team will guide you through each step."
    }, {
      question: 'What vaccinations are required?',
      answer: "Meningitis (ACWY) vaccination is mandatory for all pilgrims. COVID-19 vaccination may also be required based on current Saudi regulations. We'll provide updated requirements during your booking process."
    }, {
      question: 'What should I pack for Umrah?',
      answer: "Essential items include Ihram garments for men, modest clothing for women, comfortable walking shoes, personal medications, travel adapter, and basic toiletries. We'll provide a detailed packing list after booking."
    }, {
      question: 'How physically demanding is this tour?',
      answer: "The tour involves moderate walking, particularly during the Tawaf and Sa'i rituals. We recommend travelers be able to walk at a leisurely pace for 2-3 hours. Wheelchair services are available upon request for those who need assistance."
    }, {
      question: 'Can I extend my stay?',
      answer: 'Yes, stay extensions in either Makkah or Madinah can be arranged, subject to hotel availability and visa regulations. Additional costs will apply. Please request extensions at least 30 days before departure.'
    }],
    reviews: [{
      name: 'Ahmad Mahmoud',
      date: 'March 2023',
      rating: 5,
      comment: 'An incredible spiritual journey with excellent organization. The accommodations were superb and our guide was knowledgeable and patient. Would highly recommend this package!'
    }, {
      name: 'Fatima Rahman',
      date: 'February 2023',
      rating: 4,
      comment: 'Very good experience overall. The hotels were conveniently located and comfortable. Only suggestion would be to have more free time in Madinah.'
    }, {
      name: 'Mohammed Ali',
      date: 'January 2023',
      rating: 5,
      comment: 'This was my first Umrah and Kembara Ustaz made it a seamless experience. From pre-departure briefings to on-ground support, everything was well organized.'
    }],
    upcomingDates: [{
      date: '15 Dec 2023',
      availability: 'Available',
      price: 5057
    }, {
      date: '12 Jan 2024',
      availability: 'Limited',
      price: 5199
    }, {
      date: '10 Feb 2024',
      availability: 'Available',
      price: 5299
    }, {
      date: '15 Mar 2024',
      availability: 'Available',
      price: 5057
    }, {
      date: '12 Apr 2024',
      availability: 'Full',
      price: 4899
    }],
    images: ['https://images.unsplash.com/photo-1591221668516-0d6c8c7eb11b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80', 'https://images.unsplash.com/photo-1564769625905-50e93615e769?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1374&q=80', 'https://images.unsplash.com/photo-1565689876697-e467b6c54da2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1374&q=80', 'https://images.unsplash.com/photo-1604867688350-0499c0f5cf3d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1374&q=80']
  };
  const toggleFaq = (index: number) => {
    if (openFaqs.includes(index)) {
      setOpenFaqs(openFaqs.filter(i => i !== index));
    } else {
      setOpenFaqs([...openFaqs, index]);
    }
  };
  const incrementAdults = () => setAdults(prev => Math.min(prev + 1, 10));
  const decrementAdults = () => setAdults(prev => Math.max(prev - 1, 1));
  const incrementChildren = () => setChildren(prev => Math.min(prev + 1, 10));
  const decrementChildren = () => setChildren(prev => Math.max(prev - 1, 0));
  return <div className="w-full bg-white" data-id="element-1016">
      {/* Hero Section */}
      <div className="relative h-[60vh] bg-cover bg-center" style={{
      backgroundImage: `url(${tourData.images[0]})`
    }} data-id="element-1017">
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-end" data-id="element-1018">
          <div className="container mx-auto px-4 md:px-8 py-12" data-id="element-1019">
            <div className="mb-4" data-id="element-1020">
              <Link to="/wisata" className="inline-flex items-center text-white hover:text-gold-300 mb-4" data-id="element-1021">
                <ArrowLeft size={16} className="mr-2" data-id="element-1022" />
                Back to Tours
              </Link>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-2" data-id="element-1023">
              {tourData.name}
            </h1>
            <p className="text-xl text-white mb-6" data-id="element-1024">{tourData.tagline}</p>
            <div className="flex flex-wrap items-center gap-6 text-white" data-id="element-1025">
              <div className="flex items-center" data-id="element-1026">
                <Clock size={20} className="mr-2 text-gold-400" data-id="element-1027" />
                <span data-id="element-1028">{tourData.duration}</span>
              </div>
              <div className="flex items-center" data-id="element-1029">
                <MapPin size={20} className="mr-2 text-gold-400" data-id="element-1030" />
                <span data-id="element-1031">{tourData.cities}</span>
              </div>
              <div className="flex items-center" data-id="element-1032">
                <Users size={20} className="mr-2 text-gold-400" data-id="element-1033" />
                <span data-id="element-1034">{tourData.groupSize}</span>
              </div>
              <div className="flex items-center" data-id="element-1035">
                <Star size={20} className="mr-2 text-gold-400" fill="#e5a838" data-id="element-1036" />
                <span data-id="element-1037">
                  {tourData.rating} ({tourData.reviewCount} reviews)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 py-12" data-id="element-1038">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1039">
          {/* Left Column - Tour Details */}
          <div className="lg:w-2/3" data-id="element-1040">
            {/* Image Gallery */}
            <div className="mb-10 grid grid-cols-4 gap-3" data-id="element-1041">
              {tourData.images.map((image, index) => <div key={index} className={`${index === 0 ? 'col-span-4' : 'col-span-4 md:col-span-2 lg:col-span-4/3'} h-48 md:h-64 overflow-hidden rounded-lg`} data-id="element-1042">
                  <img src={image} alt={`${tourData.name} - Image ${index + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-id="element-1043" />
                </div>)}
            </div>
            {/* Description */}
            <div className="mb-10" data-id="element-1044">
              <h2 className="text-2xl font-bold text-navy-900 mb-4" data-id="element-1045">
                Tour Overview
              </h2>
              <p className="text-navy-700 leading-relaxed mb-6" data-id="element-1046">
                {tourData.description}
              </p>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1047">
                Highlights
              </h3>
              <div className="grid md:grid-cols-2 gap-2 mb-6" data-id="element-1048">
                {tourData.highlights.map((highlight, index) => <div key={index} className="flex items-start" data-id="element-1049">
                    <Check size={18} className="text-gold-500 mr-2 mt-1 flex-shrink-0" data-id="element-1050" />
                    <span className="text-navy-700" data-id="element-1051">{highlight}</span>
                  </div>)}
              </div>
            </div>
            {/* Tabs Navigation */}
            <div className="border-b border-gray-200 mb-8" data-id="element-1052">
              <div className="flex flex-wrap -mb-px" data-id="element-1053">
                <button onClick={() => setActiveTab('itinerary')} className={`inline-block py-4 px-6 text-sm font-medium ${activeTab === 'itinerary' ? 'border-b-2 border-gold-500 text-gold-500' : 'text-navy-700 hover:text-navy-900'}`} data-id="element-1054">
                  Itinerary
                </button>
                <button onClick={() => setActiveTab('inclusions')} className={`inline-block py-4 px-6 text-sm font-medium ${activeTab === 'inclusions' ? 'border-b-2 border-gold-500 text-gold-500' : 'text-navy-700 hover:text-navy-900'}`} data-id="element-1055">
                  Inclusions & Exclusions
                </button>
                <button onClick={() => setActiveTab('faqs')} className={`inline-block py-4 px-6 text-sm font-medium ${activeTab === 'faqs' ? 'border-b-2 border-gold-500 text-gold-500' : 'text-navy-700 hover:text-navy-900'}`} data-id="element-1056">
                  FAQs
                </button>
                <button onClick={() => setActiveTab('reviews')} className={`inline-block py-4 px-6 text-sm font-medium ${activeTab === 'reviews' ? 'border-b-2 border-gold-500 text-gold-500' : 'text-navy-700 hover:text-navy-900'}`} data-id="element-1057">
                  Reviews
                </button>
              </div>
            </div>
            {/* Tab Content */}
            <div className="mb-12" data-id="element-1058">
              {/* Itinerary Tab */}
              {activeTab === 'itinerary' && <div data-id="element-1059">
                  <h3 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-1060">
                    Day-by-Day Itinerary
                  </h3>
                  <div className="space-y-6" data-id="element-1061">
                    {tourData.itinerary.map((day, index) => <div key={index} className="border-l-4 border-gold-500 pl-6 py-2" data-id="element-1062">
                        <h4 className="text-lg font-bold text-navy-900" data-id="element-1063">
                          Day {day.day}: {day.title}
                        </h4>
                        <p className="text-navy-700 my-2" data-id="element-1064">{day.description}</p>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-navy-600" data-id="element-1065">
                          <div className="flex items-center" data-id="element-1066">
                            <Utensils size={16} className="mr-2 text-gold-500" data-id="element-1067" />
                            <span data-id="element-1068">
                              Meals:{' '}
                              {day.meals.length === 0 ? 'Not included' : day.meals.map(meal => meal.charAt(0).toUpperCase() + meal.slice(1)).join(', ')}
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-1069">
                            <Hotel size={16} className="mr-2 text-gold-500" data-id="element-1070" />
                            <span data-id="element-1071">Accommodation: {day.accommodation}</span>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}
              {/* Inclusions Tab */}
              {activeTab === 'inclusions' && <div data-id="element-1072">
                  <div className="grid md:grid-cols-2 gap-8" data-id="element-1073">
                    <div data-id="element-1074">
                      <h3 className="text-xl font-bold text-navy-900 mb-4" data-id="element-1075">
                        What's Included
                      </h3>
                      <ul className="space-y-2" data-id="element-1076">
                        {tourData.inclusions.map((item, index) => <li key={index} className="flex items-start" data-id="element-1077">
                            <Check size={18} className="text-green-500 mr-2 mt-1" data-id="element-1078" />
                            <span className="text-navy-700" data-id="element-1079">{item}</span>
                          </li>)}
                      </ul>
                    </div>
                    <div data-id="element-1080">
                      <h3 className="text-xl font-bold text-navy-900 mb-4" data-id="element-1081">
                        What's Not Included
                      </h3>
                      <ul className="space-y-2" data-id="element-1082">
                        {tourData.exclusions.map((item, index) => <li key={index} className="flex items-start" data-id="element-1083">
                            <ChevronRight size={18} className="text-red-500 mr-2 mt-1" data-id="element-1084" />
                            <span className="text-navy-700" data-id="element-1085">{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-10 bg-navy-50 p-6 rounded-lg" data-id="element-1086">
                    <h3 className="text-xl font-bold text-navy-900 mb-4" data-id="element-1087">
                      Important Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-1088">
                      <div data-id="element-1089">
                        <h4 className="font-bold text-navy-900 mb-2" data-id="element-1090">
                          Passport & Visa Requirements
                        </h4>
                        <p className="text-navy-700 text-sm" data-id="element-1091">
                          Valid passport with at least 6 months validity from
                          return date. We'll handle the Umrah visa process for
                          you.
                        </p>
                      </div>
                      <div data-id="element-1092">
                        <h4 className="font-bold text-navy-900 mb-2" data-id="element-1093">
                          Health & Vaccination
                        </h4>
                        <p className="text-navy-700 text-sm" data-id="element-1094">
                          Meningitis (ACWY) vaccination required. COVID-19
                          requirements as per current Saudi regulations.
                        </p>
                      </div>
                      <div data-id="element-1095">
                        <h4 className="font-bold text-navy-900 mb-2" data-id="element-1096">
                          Cancellation Policy
                        </h4>
                        <p className="text-navy-700 text-sm" data-id="element-1097">
                          60+ days: Full refund minus deposit 30-59 days: 50%
                          refund 0-29 days: No refund
                        </p>
                      </div>
                      <div data-id="element-1098">
                        <h4 className="font-bold text-navy-900 mb-2" data-id="element-1099">
                          Travel Insurance
                        </h4>
                        <p className="text-navy-700 text-sm" data-id="element-1100">
                          Comprehensive travel insurance strongly recommended.
                          We can arrange this for you at an additional cost.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>}
              {/* FAQs Tab */}
              {activeTab === 'faqs' && <div data-id="element-1101">
                  <h3 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-1102">
                    Frequently Asked Questions
                  </h3>
                  <div className="space-y-4" data-id="element-1103">
                    {tourData.faqs.map((faq, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1104">
                        <button className="w-full flex justify-between items-center p-4 text-left font-medium text-navy-900 hover:bg-gray-50" onClick={() => toggleFaq(index)} data-id="element-1105">
                          {faq.question}
                          {openFaqs.includes(index) ? <ChevronUp size={20} data-id="element-1106" /> : <ChevronDown size={20} data-id="element-1107" />}
                        </button>
                        {openFaqs.includes(index) && <div className="p-4 bg-gray-50 border-t border-gray-200" data-id="element-1108">
                            <p className="text-navy-700" data-id="element-1109">{faq.answer}</p>
                          </div>}
                      </div>)}
                  </div>
                </div>}
              {/* Reviews Tab */}
              {activeTab === 'reviews' && <div data-id="element-1110">
                  <div className="flex justify-between items-center mb-6" data-id="element-1111">
                    <h3 className="text-2xl font-bold text-navy-900" data-id="element-1112">
                      Customer Reviews
                    </h3>
                    <div className="flex items-center" data-id="element-1113">
                      <Star size={20} className="text-gold-500 mr-1" fill="#e5a838" data-id="element-1114" />
                      <span className="font-bold text-navy-900" data-id="element-1115">
                        {tourData.rating}
                      </span>
                      <span className="text-navy-600 ml-1" data-id="element-1116">
                        ({tourData.reviewCount} reviews)
                      </span>
                    </div>
                  </div>
                  <div className="space-y-6" data-id="element-1117">
                    {tourData.reviews.map((review, index) => <div key={index} className="border-b border-gray-200 pb-6" data-id="element-1118">
                        <div className="flex justify-between mb-2" data-id="element-1119">
                          <h4 className="font-bold text-navy-900" data-id="element-1120">
                            {review.name}
                          </h4>
                          <span className="text-navy-600 text-sm" data-id="element-1121">
                            {review.date}
                          </span>
                        </div>
                        <div className="flex items-center mb-3" data-id="element-1122">
                          {[...Array(5)].map((_, i) => <Star key={i} size={16} className={i < review.rating ? 'text-gold-500' : 'text-gray-300'} fill={i < review.rating ? '#e5a838' : '#d1d5db'} data-id="element-1123" />)}
                        </div>
                        <p className="text-navy-700" data-id="element-1124">{review.comment}</p>
                      </div>)}
                  </div>
                </div>}
            </div>
            {/* Related Tours */}
            <div data-id="element-1125">
              <h3 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-1126">
                You May Also Like
              </h3>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-1127">
                <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-1128">
                  <div className="h-48 overflow-hidden" data-id="element-1129">
                    <img src="https://images.unsplash.com/photo-1565689876697-e467b6c54da2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1374&q=80" alt="Essential Umrah Package" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-id="element-1130" />
                  </div>
                  <div className="p-4" data-id="element-1131">
                    <h4 className="font-bold text-navy-900" data-id="element-1132">
                      Essential Umrah Package
                    </h4>
                    <p className="text-navy-600 text-sm mb-2" data-id="element-1133">
                      9 DAYS | 4N MAKKAH | 3N MADINAH
                    </p>
                    <div className="flex justify-between items-center" data-id="element-1134">
                      <div data-id="element-1135">
                        <span className="text-sm text-navy-600" data-id="element-1136">From</span>
                        <div className="font-bold text-gold-500" data-id="element-1137">$4,299</div>
                      </div>
                      <Link to="/tour/essential-umrah" className="text-navy-900 hover:text-gold-500 font-medium text-sm" data-id="element-1138">
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-1139">
                  <div className="h-48 overflow-hidden" data-id="element-1140">
                    <img src="https://images.unsplash.com/photo-1564769625905-50e93615e769?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1374&q=80" alt="Extended Umrah Experience" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-id="element-1141" />
                  </div>
                  <div className="p-4" data-id="element-1142">
                    <h4 className="font-bold text-navy-900" data-id="element-1143">
                      Extended Umrah Experience
                    </h4>
                    <p className="text-navy-600 text-sm mb-2" data-id="element-1144">
                      14 DAYS | 7N MAKKAH | 5N MADINAH
                    </p>
                    <div className="flex justify-between items-center" data-id="element-1145">
                      <div data-id="element-1146">
                        <span className="text-sm text-navy-600" data-id="element-1147">From</span>
                        <div className="font-bold text-gold-500" data-id="element-1148">$6,299</div>
                      </div>
                      <Link to="/tour/extended-umrah" className="text-navy-900 hover:text-gold-500 font-medium text-sm" data-id="element-1149">
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Column - Booking Widget */}
          <div className="lg:w-1/3" data-id="element-1150">
            <div className="sticky top-8" data-id="element-1151">
              <div className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden" data-id="element-1152">
                <div className="p-6" data-id="element-1153">
                  <div className="flex justify-between items-center mb-4" data-id="element-1154">
                    <div data-id="element-1155">
                      <span className="text-sm text-navy-600" data-id="element-1156">From</span>
                      <div className="text-3xl font-bold text-navy-900" data-id="element-1157">
                        ${tourData.price}
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-1158">
                      <Star size={20} className="text-gold-500 mr-1" fill="#e5a838" data-id="element-1159" />
                      <span className="font-bold text-navy-900" data-id="element-1160">
                        {tourData.rating}
                      </span>
                    </div>
                  </div>
                  {/* Booking Form */}
                  <form data-id="element-1161">
                    <div className="mb-4" data-id="element-1162">
                      <label className="block text-navy-900 font-medium mb-2" data-id="element-1163">
                        Departure Date
                      </label>
                      <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-1164">
                        <option value="" data-id="element-1165">Select a date</option>
                        {tourData.upcomingDates.map((date, index) => <option key={index} value={date.date} disabled={date.availability === 'Full'} data-id="element-1166">
                            {date.date} - ${date.price} ({date.availability})
                          </option>)}
                      </select>
                    </div>
                    <div className="mb-4" data-id="element-1167">
                      <label className="block text-navy-900 font-medium mb-2" data-id="element-1168">
                        Travelers
                      </label>
                      <div className="grid grid-cols-2 gap-4" data-id="element-1169">
                        <div data-id="element-1170">
                          <label className="block text-navy-700 text-sm mb-1" data-id="element-1171">
                            Adults
                          </label>
                          <div className="flex border border-gray-300 rounded-md overflow-hidden" data-id="element-1172">
                            <button type="button" onClick={decrementAdults} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-1173">
                              -
                            </button>
                            <input type="number" value={adults} readOnly className="w-full p-2 text-center focus:outline-none" data-id="element-1174" />
                            <button type="button" onClick={incrementAdults} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-1175">
                              +
                            </button>
                          </div>
                        </div>
                        <div data-id="element-1176">
                          <label className="block text-navy-700 text-sm mb-1" data-id="element-1177">
                            Children
                          </label>
                          <div className="flex border border-gray-300 rounded-md overflow-hidden" data-id="element-1178">
                            <button type="button" onClick={decrementChildren} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-1179">
                              -
                            </button>
                            <input type="number" value={children} readOnly className="w-full p-2 text-center focus:outline-none" data-id="element-1180" />
                            <button type="button" onClick={incrementChildren} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-1181">
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200 mt-4" data-id="element-1182">
                      <div className="flex justify-between mb-2" data-id="element-1183">
                        <span className="text-navy-700" data-id="element-1184">Base Price</span>
                        <span className="text-navy-900" data-id="element-1185">
                          ${tourData.price} x {adults + children}
                        </span>
                      </div>
                      <div className="flex justify-between mb-2" data-id="element-1186">
                        <span className="text-navy-700" data-id="element-1187">Taxes & Fees</span>
                        <span className="text-navy-900" data-id="element-1188">$399</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t border-gray-200" data-id="element-1189">
                        <span className="text-navy-900" data-id="element-1190">Total</span>
                        <span className="text-navy-900" data-id="element-1191">
                          ${tourData.price * (adults + children) + 399}
                        </span>
                      </div>
                    </div>
                    <button type="submit" className="w-full bg-gold-500 hover:bg-gold-600 text-white py-3 rounded-md font-medium transition-colors mt-6" data-id="element-1192">
                      Book Now
                    </button>
                    <button type="button" className="w-full border border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white py-3 rounded-md font-medium transition-colors mt-3" data-id="element-1193">
                      Request Custom Quote
                    </button>
                  </form>
                </div>
                <div className="bg-navy-50 p-6" data-id="element-1194">
                  <h4 className="font-bold text-navy-900 mb-4" data-id="element-1195">Need Help?</h4>
                  <div className="space-y-3" data-id="element-1196">
                    <div className="flex items-center" data-id="element-1197">
                      <Phone size={20} className="text-gold-500 mr-3" data-id="element-1198" />
                      <span className="text-navy-700" data-id="element-1199">+65 6721 3357</span>
                    </div>
                    <div className="flex items-center" data-id="element-1200">
                      <Mail size={20} className="text-gold-500 mr-3" data-id="element-1201" />
                      <span className="text-navy-700" data-id="element-1202">
                        info@kembarauztaz.com
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between mt-6" data-id="element-1203">
                    <button className="flex items-center text-navy-700 hover:text-navy-900" data-id="element-1204">
                      <Share2 size={18} className="mr-1" data-id="element-1205" />
                      <span className="text-sm" data-id="element-1206">Share</span>
                    </button>
                    <button className="flex items-center text-navy-700 hover:text-navy-900" data-id="element-1207">
                      <Heart size={18} className="mr-1" data-id="element-1208" />
                      <span className="text-sm" data-id="element-1209">Save</span>
                    </button>
                    <button className="flex items-center text-navy-700 hover:text-navy-900" data-id="element-1210">
                      <Plane size={18} className="mr-1" data-id="element-1211" />
                      <span className="text-sm" data-id="element-1212">Compare</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default TourDetail;